import React, { useState, useEffect, useMemo, useRef } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { ConfirmRemoveBundleDialog } from '@/components/ConfirmRemoveBundleDialog';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Scan, Check, X, AlertTriangle, Loader2, Truck,
  User as UserIcon, MapPin, Receipt, ChevronDown, Trash2, Sparkles,
  ListChecks, RotateCw, Printer, Usb, RefreshCw, Tags, IndianRupee, Boxes,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as printerApi from '@/services/printerApi';
import { useUSNReplacement } from '@/hooks/useUSNReplacement';
import { generateTsplFromLabelData, PRINTER_CONFIGS, templateHasLayout } from '@/services/tsplLabel';
import * as networkPrintApi from '@/services/networkPrintApi';
import NetworkPrinterDialog from '@/components/print/NetworkPrinterDialog';
import { Network as NetworkIcon } from 'lucide-react';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

const QUALITY_OPTIONS = [
  { value: 'PERFECT', label: 'Perfect', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { value: 'DAMAGED', label: 'Damaged', color: 'bg-red-100 text-red-700 border-red-300' },
  { value: 'REFINISH', label: 'Refinish', color: 'bg-amber-100 text-amber-700 border-amber-300' },
];

const SalesReturnBundleReceiptEntry = () => {
  const navigate = useNavigate();

  // Master data
  const [customers, setCustomers] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [agents, setAgents] = useState([]);
  const [reasons, setReasons] = useState([]);

  // Header form state
  const [customerCode, setCustomerCode] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [place, setPlace] = useState('');
  const [transporterId, setTransporterId] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [freightCharge, setFreightCharge] = useState('');
  const [agentName, setAgentName] = useState('');
  const [returnReason, setReturnReason] = useState('CUSTOMER_REJECTION');
  const [returnReasonNote, setReturnReasonNote] = useState('');
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState('');

  // 2026-06-12 — filter the FULL customer list (25k+) ourselves, then render
  // only the top matches. The old `customers.slice(0, 800)` made customers
  // beyond the first 800 unfindable (cmdk only searches rendered items).
  const filteredCustomers = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const list = !q ? customers : customers.filter((c) => {
      const code = c.ginesys_id || c.id || c.customer_code || '';
      const name = c.display_name || c.customer_name || c.legal_name || c.trade_name || '';
      const cplace = c.place || c.billing_place || '';
      return `${name} ${cplace} ${c.gst || ''} ${code}`.toLowerCase().includes(q);
    });
    return list.slice(0, 500);
  }, [customers, customerQuery]);

  // Active receipt
  const [receipt, setReceipt] = useState(null);
  const [usns, setUsns] = useState([]);
  const [isCreatingReceipt, setIsCreatingReceipt] = useState(false);

  // Bundle scan
  const [bundleInput, setBundleInput] = useState('');
  const [activeBundle, setActiveBundle] = useState('');
  const bundleInputRef = useRef(null);

  // USN scan
  const [usnInput, setUsnInput] = useState('');
  const [scanningUsn, setScanningUsn] = useState(false);
  const usnInputRef = useRef(null);

  // FG-assignment dialog (for FRESH USNs without master enrichment)
  const [fgPromptUsn, setFgPromptUsn] = useState(null); // {usn_code}
  const [fgSku, setFgSku] = useState('');
  const [fgDesign, setFgDesign] = useState('');
  const [fgShade, setFgShade] = useState('');
  const [fgSize, setFgSize] = useState('');

  // Combo unbundle helper
  const [unbundlingUsn, setUnbundlingUsn] = useState(null);

  // Finalize
  const [isFinalizing, setIsFinalizing] = useState(false);

  // --- Item-wise summary + main-label reprint (new MRP stickers) ---
  const { openReplaceDialog } = useUSNReplacement();
  const [itemSummary, setItemSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [pricingBySku, setPricingBySku] = useState({}); // { sku_code: pricing_type }
  const [printingSku, setPrintingSku] = useState(null);
  const [usbDevice, setUsbDevice] = useState(null);
  const [usbSupported, setUsbSupported] = useState(true);
  const [arialLineSpacingMultiplier, setArialLineSpacingMultiplier] = useState(2.5);
  // Combo box-sticker second step — { sku, boxTemplate, label_data, mrp_formatted, newUsns[] }
  const [pendingBoxPrint, setPendingBoxPrint] = useState(null);
  const printerModel = 'TSC_TE210';

  // Print method: 'usb' (WebUSB, existing) or 'network' (Print Bridge). Additive.
  const [printMethod, setPrintMethod] = useState(() => localStorage.getItem('sr_print_method') || 'usb');
  const [netPrinter, setNetPrinter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sr_net_printer') || 'null'); } catch (_) { return null; }
  });
  const [printerPickerOpen, setPrinterPickerOpen] = useState(false);
  const printerPickerResolve = useRef(null);

  // Publish this screen's USB/Network choice as the active print target so the
  // shared printer service routes correctly (per-screen, independent of others).
  useEffect(() => {
    localStorage.setItem('lp_active_method', printMethod);
    if (netPrinter) localStorage.setItem('lp_active_printer', JSON.stringify(netPrinter));
    else localStorage.removeItem('lp_active_printer');
  }, [printMethod, netPrinter]);

  // Bootstrap masters
  useEffect(() => {
    Promise.all([
      // lite=true: full list, dropdown fields only, no per-customer credit
      // enrichment. The old page_size=20000 call did 20k sequential credit
      // lookups (~25MB payload) and timed out on production → empty dropdown.
      apiClient.get('/order-management/customer-master?lite=true').catch(() => ({ data: { customers: [] } })),
      apiClient.get('/transport/active').catch(() => ({ data: { transports: [] } })),
      apiClient.get('/sales-return/agents').catch(() => ({ data: { agents: [] } })),
      apiClient.get('/sales-return/return-reasons').catch(() => ({ data: { reasons: [] } })),
    ]).then(([cs, ts, ags, rs]) => {
      setCustomers(cs.data.customers || cs.data.entries || []);
      setTransporters(ts.data.transports || ts.data.entries || []);
      setAgents(ags.data.agents || []);
      setReasons(rs.data.reasons || []);
    });
  }, []);

  useEffect(() => {
    if (receipt && bundleInputRef.current) bundleInputRef.current.focus();
  }, [receipt]);

  useEffect(() => {
    if (activeBundle && usnInputRef.current) usnInputRef.current.focus();
  }, [activeBundle]);

  const headerComplete = customerCode && place && agentName;

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    autoSave, clearDraft, loadDraft, removeDraft, setActiveDraftId,
  } = useOperationDraft({ moduleKey: 'section:Sales Return', operationType: 'bundle_receipt', allowZero: true });

  const [resumedDraft, setResumedDraft] = useState(null);

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setReceipt(null);
    setUsns([]);
  };

  // USNs persist server-side per scan, so the draft is a resumable bookmark of
  // the open receipt + scanned USN count.
  useEffect(() => {
    if (!receipt) return;
    autoSave({
      label: `${receipt.receipt_id} · ${customerName || place || 'receipt'}`,
      bundleCount: usns.length,
      documentId: receipt.receipt_id,
      documentNo: receipt.receipt_id,
      payload: { receiptId: receipt.receipt_id, customerName, place },
    });
  }, [receipt, usns, customerName, place, autoSave]);

  const handleResumeDraft = async (draftId) => {
    try {
      const d = await loadDraft(draftId);
      const rid = d.payload?.receiptId;
      if (!rid) return;
      const fresh = await apiClient.get(`/sales-return/receipts/${rid}`);
      setReceipt(fresh.data);
      setUsns(fresh.data.usns || []);
      if (fresh.data.customer_code) setCustomerCode(fresh.data.customer_code);
      if (fresh.data.customer_name) setCustomerName(fresh.data.customer_name);
      if (fresh.data.place) setPlace(fresh.data.place);
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  const createReceipt = async () => {
    if (!headerComplete) {
      toast.error('Customer, place and agent are required');
      return;
    }
    setIsCreatingReceipt(true);
    try {
      const res = await apiClient.post('/sales-return/receipts', {
        customer_code: customerCode,
        customer_name: customerName,
        place,
        transporter_id: transporterId || null,
        transporter_name: transporterName || null,
        freight_charge: parseFloat(freightCharge || '0') || 0,
        agent_name: agentName,
        return_reason: returnReason,
        return_reason_note: returnReasonNote || null,
      });
      setReceipt(res.data);
      setUsns([]);
      setActiveDraftId(null);
      setResumedDraft(null);
      toast.success(`Receipt ${res.data.receipt_id} opened`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create receipt');
    } finally {
      setIsCreatingReceipt(false);
    }
  };

  const handleAddBundle = async (e) => {
    e?.preventDefault?.();
    const code = bundleInput.trim();
    if (!code) return;
    try {
      await apiClient.post(`/sales-return/receipts/${receipt.receipt_id}/scan-bundle`, {
        bundle_barcode: code,
      });
      setReceipt((r) => ({ ...r, bundles: [...(r.bundles || []), code] }));
      setActiveBundle(code);
      setBundleInput('');
      toast.success(`Bundle ${code} added`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add bundle', {
        duration: 9000,
      });
    }
  };

  const handleScanUsn = async (e) => {
    e?.preventDefault?.();
    const code = usnInput.trim();
    if (!code || !activeBundle) return;
    setScanningUsn(true);
    try {
      const res = await apiClient.post(`/sales-return/receipts/${receipt.receipt_id}/scan-usn`, {
        bundle_barcode: activeBundle,
        usn_code: code,
      });
      const row = res.data;
      setUsns((prev) => [...prev, row]);
      setUsnInput('');
      if (row.needs_fg_assignment) {
        setFgPromptUsn(row);
        setFgSku('');
        setFgDesign('');
        setFgShade('');
        setFgSize('');
        toast.message(`USN ${row.usn_code} is fresh — please assign FG details`);
      } else {
        toast.success(
          `${row.usn_code} → ${row.source}${row.sku_code ? ` · ${row.sku_code}` : ''}`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanningUsn(false);
      // Keep focus on the USN input so the next barcode scan lands here
      // immediately without manual click — only when no FG prompt blocks us.
      setTimeout(() => {
        if (usnInputRef.current && !document.querySelector('[data-fg-prompt-open]')) {
          usnInputRef.current.focus();
        }
      }, 0);
    }
  };

  const submitFgAssignment = async () => {
    if (!fgSku.trim()) {
      toast.error('FG / SKU code is required');
      return;
    }
    try {
      await apiClient.post(
        `/sales-return/receipts/${receipt.receipt_id}/usns/${fgPromptUsn.usn_code}/assign-fg`,
        {
          usn_code: fgPromptUsn.usn_code,
          sku_code: fgSku.trim(),
          design_name: fgDesign.trim() || null,
          shade: fgShade.trim() || null,
          size: fgSize.trim() || null,
        },
      );
      setUsns((prev) => prev.map((u) => u.usn_code === fgPromptUsn.usn_code
        ? { ...u, sku_code: fgSku.trim(), design_name: fgDesign, shade: fgShade, size: fgSize, needs_fg_assignment: false }
        : u));
      setFgPromptUsn(null);
      toast.success('FG details saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save FG');
    }
  };

  const setQuality = async (usnCode, quality) => {
    try {
      await apiClient.patch(
        `/sales-return/receipts/${receipt.receipt_id}/usns/${usnCode}/quality`,
        { quality_status: quality },
      );
      setUsns((prev) => prev.map((u) =>
        u.usn_code === usnCode ? { ...u, quality_status: quality } : u
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set quality');
    }
  };

  const [removeUsnTarget, setRemoveUsnTarget] = useState(null);

  const removeUsn = async (usnCode) => {
    try {
      await apiClient.delete(`/sales-return/receipts/${receipt.receipt_id}/usns/${usnCode}`);
      setUsns((prev) => prev.filter((u) => u.usn_code !== usnCode));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const unbundleCombo = async (usnCode) => {
    setUnbundlingUsn(usnCode);
    try {
      const res = await apiClient.post(
        `/sales-return/receipts/${receipt.receipt_id}/usns/${usnCode}/unbundle-combo`,
      );
      setUsns((prev) => {
        const without = prev.filter((u) => u.usn_code !== usnCode);
        return [...without, ...(res.data.components || [])];
      });
      toast.success(`Combo split into ${res.data.components.length} components`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot unbundle combo');
    } finally {
      setUnbundlingUsn(null);
    }
  };

  const finalize = async () => {
    if (!usns.length) {
      toast.error('Scan at least one USN before finalizing');
      return;
    }
    setIsFinalizing(true);
    try {
      // Re-sync from the server first — the on-screen list only tracks
      // in-session scans and can drift from the DB (combo unbundle, resume,
      // a quality update that didn't persist). This guarantees the count and
      // pending-tag state match the database before we attempt to finalize.
      const fresh = await apiClient.get(`/sales-return/receipts/${receipt.receipt_id}`);
      const serverUsns = fresh.data.usns || [];
      setUsns(serverUsns);

      const untaggedRows = serverUsns.filter((u) => !u.quality_status);
      if (untaggedRows.length) {
        const codes = untaggedRows.map((u) => u.usn_code).slice(0, 10).join(', ');
        const more = untaggedRows.length > 10 ? ` (+${untaggedRows.length - 10} more)` : '';
        toast.error(
          `${untaggedRows.length} USN(s) still missing quality status: ${codes}${more}`,
          { duration: 10000 }
        );
        setIsFinalizing(false);
        return;
      }
      const needsFgRows = serverUsns.filter((u) => u.needs_fg_assignment);
      if (needsFgRows.length) {
        const codes = needsFgRows.map((u) => u.usn_code).slice(0, 10).join(', ');
        const more = needsFgRows.length > 10 ? ` (+${needsFgRows.length - 10} more)` : '';
        toast.error(
          `${needsFgRows.length} USN(s) still missing FG details: ${codes}${more}`,
          { duration: 10000 }
        );
        setIsFinalizing(false);
        return;
      }

      const res = await apiClient.post(`/sales-return/receipts/${receipt.receipt_id}/finalize`);
      const t = res.data.totals;
      toast.success(
        `Finalized — Perfect ${t.perfect}, Damaged ${t.damaged}, Refinish ${t.refinish}`
      );
      // Reset for the next receipt
      clearDraft();
      setReceipt(null);
      setUsns([]);
      setResumedDraft(null);
      setActiveBundle('');
      setBundleInput('');
      setUsnInput('');
      setFreightCharge('');
      setReturnReasonNote('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const msg = detail
        ? `Finalize failed: ${detail}`
        : status
          ? `Finalize failed (HTTP ${status}) — please share with admin`
          : `Finalize failed: ${err.message || 'network error'}`;
      toast.error(msg, { duration: 10000 });
    } finally {
      setIsFinalizing(false);
    }
  };

  // Detect WebUSB + load line-spacing setting once.
  useEffect(() => {
    setUsbSupported(printerApi.isWebUSBSupported());
    apiClient.get('/print-settings')
      .then((r) => {
        const m = r.data?.settings?.arial_line_spacing_multiplier;
        if (m) setArialLineSpacingMultiplier(m);
      })
      .catch(() => {});
  }, []);

  const fetchItemSummary = async () => {
    if (!receipt) { setItemSummary([]); return; }
    setSummaryLoading(true);
    try {
      const res = await apiClient.get(`/sales-return/receipts/${receipt.receipt_id}/item-summary`);
      const items = res.data.items || [];
      setItemSummary(items);
      setPricingBySku((prev) => {
        const next = { ...prev };
        items.forEach((it) => {
          if (!it.sku_code) return;
          if (!next[it.sku_code]) {
            next[it.sku_code] = it.available_pricing_types?.includes('TN')
              ? 'TN'
              : (it.available_pricing_types?.[0] || 'TN');
          }
        });
        return next;
      });
    } catch (err) {
      // Non-blocking — summary is a helper panel.
      console.error('Failed to load item summary', err);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Refresh the summary shortly after the scanned set changes.
  useEffect(() => {
    if (!receipt) { setItemSummary([]); return; }
    const t = setTimeout(() => fetchItemSummary(), 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usns.length, receipt?.receipt_id]);

  const connectPrinter = async () => {
    try {
      const device = await printerApi.requestUSBPrinter();
      setUsbDevice(device);
      toast.success(`Connected to ${device.productName || 'USB Printer'}`);
      return device;
    } catch (err) {
      toast.error('Failed to connect USB printer. Please try again.');
      return null;
    }
  };

  const changePrintMethod = (m) => {
    setPrintMethod(m);
    localStorage.setItem('sr_print_method', m);
  };

  // Resolve/open the network printer picker. Returns the chosen printer or null.
  const ensureNetPrinter = (forcePicker = false) => {
    if (!forcePicker && netPrinter) return Promise.resolve(netPrinter);
    return new Promise((resolve) => {
      printerPickerResolve.current = resolve;
      setPrinterPickerOpen(true);
    });
  };

  const handlePrinterPicked = (sel) => {
    const selection = { agentId: sel.agentId, printerName: sel.printerName, agentName: sel.agentName };
    setNetPrinter(selection);
    localStorage.setItem('sr_net_printer', JSON.stringify(selection));
    // Persist as the user's server-side default immediately (selection-time).
    networkPrintApi.setPrintPreference({ agentId: sel.agentId, printerName: sel.printerName }).catch(() => {});
    setPrinterPickerOpen(false);
    if (printerPickerResolve.current) { printerPickerResolve.current(selection); printerPickerResolve.current = null; }
  };

  const handlePrinterDialogClose = (open) => {
    if (open) return;
    setPrinterPickerOpen(false);
    if (printerPickerResolve.current) { printerPickerResolve.current(null); printerPickerResolve.current = null; }
  };

  // Unified send: routes the TSPL blob to USB (existing) or the network bridge.
  const sendBlobToPrinter = async (blob, { labelType = null, meta = null, forcePicker = false } = {}) => {
    if (printMethod === 'network') {
      let sel = netPrinter;
      if (forcePicker || !sel) {
        sel = await ensureNetPrinter(true);
        if (!sel) return { success: false, error: 'No printer selected' };
      }
      try {
        return await networkPrintApi.printViaAgent({
          agentId: sel.agentId, printerName: sel.printerName, payload: blob, labelType, meta,
        });
      } catch (e) {
        return { success: false, error: e.response?.data?.detail || e.message || 'Network print error' };
      }
    }
    // USB (WebUSB) — unchanged behaviour.
    let device = usbDevice;
    if (forcePicker || !device) {
      device = await connectPrinter();
      if (!device) return { success: false, error: 'No USB printer connected' };
    }
    try {
      await printerApi.sendToUSBPrinter(device, blob);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message || 'USB print error' };
    }
  };

  const currentMrpFor = (item) => {
    const pt = pricingBySku[item.sku_code] || 'TN';
    const m = item.mrp_by_pricing_type?.[pt];
    return (m === undefined || m === null) ? item.current_mrp : m;
  };

  // Reprint (mint fresh USN + new MRP) all pieces of one SKU group, then
  // stream TSPL to the connected label printer.
  const handlePrintGroup = async (item) => {
    if (!item.sku_code) {
      toast.error('Assign FG details for this item before printing.');
      return;
    }
    if (printMethod === 'usb') {
      if (!usbDevice) {
        const d = await connectPrinter();
        if (!d) return;
      }
    } else {
      const sel = await ensureNetPrinter(false);
      if (!sel) return;
    }
    const pricing_type = pricingBySku[item.sku_code] || 'TN';
    const dialog = await openReplaceDialog({
      count: item.count,
      sourceFlow: 'sales_return_reprint',
      subjectLabel: `${item.sku_code} · ${item.design_name || ''} (${item.count} pcs)`.trim(),
    });
    if (!dialog) return;

    setPrintingSku(item.sku_code);
    try {
      const res = await apiClient.post(
        `/sales-return/receipts/${receipt.receipt_id}/reprint-labels`,
        { sku_code: item.sku_code, pricing_type, reason: dialog.reason },
      );
      const { replaced, failures, label_data, template, box_template, mrp_formatted, is_combo } = res.data;
      if (!replaced.length) {
        toast.error(`No labels printed. ${failures?.[0]?.detail || ''}`, { duration: 10000 });
        return;
      }
      const printerConfig = PRINTER_CONFIGS[printerModel];
      let blob = '';
      replaced.forEach((r) => {
        blob += generateTsplFromLabelData(label_data, template, r.new_usn, mrp_formatted, {
          printerConfig,
          arialLineSpacingMultiplier,
          templateType: template?.template_type,
        });
      });
      const printRes = await sendBlobToPrinter(blob, {
        labelType: 'main_label',
        meta: { sku_code: item.sku_code, receipt_id: receipt.receipt_id, count: replaced.length },
      });
      if (printRes.success) {
        toast.success(
          `Printed ${replaced.length} new-MRP label(s) for ${item.sku_code}` +
          (failures?.length ? ` · ${failures.length} failed` : '')
        );
      } else {
        toast.error(
          `Labels were re-minted, but the print failed: ${printRes.error}. Re-open and re-print if needed.`,
          { duration: 12000 },
        );
      }

      // Receipt USN codes changed (old → new). Re-sync from the server.
      try {
        const fresh = await apiClient.get(`/sales-return/receipts/${receipt.receipt_id}`);
        setUsns(fresh.data.usns || []);
      } catch (_) { /* summary refresh below still runs */ }

      if (is_combo && box_template) {
        setPendingBoxPrint({
          sku: item.sku_code,
          boxTemplate: box_template,
          label_data,
          mrp_formatted,
          newUsns: replaced.map((r) => r.new_usn),
        });
        toast.message(`Now print BOX stickers for ${item.sku_code}.`);
      } else {
        fetchItemSummary();
      }
      if (failures?.length) {
        console.warn('Reprint failures:', failures);
        toast.warning(
          `${failures.length} piece(s) could not be re-stickered — first: ${failures[0]?.detail}`,
          { duration: 10000 },
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reprint failed', { duration: 10000 });
    } finally {
      setPrintingSku(null);
    }
  };

  // Resolve the box template to one that actually has a coordinate layout.
  // If the reprint API's box template lacks field positions, re-pull the full
  // template from the master. Returns null when no usable layout exists so we
  // refuse to print a cramped/overlapping box label.
  const resolveBoxTemplate = async (boxTemplate) => {
    if (templateHasLayout(boxTemplate)) return boxTemplate;
    const norm = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    try {
      const res = await apiClient.get('/main-labels/templates');
      const all = res.data?.templates || [];
      const want = norm(boxTemplate?.template_name);
      const match = all.find((t) => norm(t.template_name) === want && templateHasLayout(t));
      if (match) return match;
    } catch (_) { /* fall through */ }
    return null;
  };

  // Second step for combos — print BOX stickers, optionally on a different printer.
  const handlePrintBoxStickers = async (useDifferentPrinter = false) => {
    if (!pendingBoxPrint) return;
    // Always print with the MASTER box template's layout, never cramped defaults.
    const boxTpl = await resolveBoxTemplate(pendingBoxPrint.boxTemplate);
    if (!boxTpl) {
      toast.error(
        `Box template "${pendingBoxPrint.boxTemplate?.template_name || '—'}" has no label layout configured in the master. Set its field positions in Admin → Main Label Templates, then reprint.`,
        { duration: 12000 },
      );
      return;
    }
    setPrintingSku(pendingBoxPrint.sku);
    try {
      const printerConfig = PRINTER_CONFIGS[printerModel];
      let blob = '';
      pendingBoxPrint.newUsns.forEach((u) => {
        blob += generateTsplFromLabelData(
          pendingBoxPrint.label_data, boxTpl, u, pendingBoxPrint.mrp_formatted,
          { printerConfig, arialLineSpacingMultiplier, templateType: boxTpl?.template_type },
        );
      });
      const printRes = await sendBlobToPrinter(blob, {
        labelType: 'box_sticker',
        meta: { sku_code: pendingBoxPrint.sku, count: pendingBoxPrint.newUsns.length },
        forcePicker: useDifferentPrinter,
      });
      if (printRes.success) {
        toast.success(`Printed ${pendingBoxPrint.newUsns.length} box sticker(s) for ${pendingBoxPrint.sku}`);
      } else {
        toast.error(`Box sticker print error: ${printRes.error}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(`Box sticker print error: ${err.message || err}`, { duration: 10000 });
    } finally {
      setPendingBoxPrint(null);
      setPrintingSku(null);
      fetchItemSummary();
    }
  };

  const handleSkipBox = () => {
    setPendingBoxPrint(null);
    fetchItemSummary();
  };

  const usnsByBundle = useMemo(() => {
    const map = {};
    usns.forEach((u) => {
      if (!map[u.bundle_barcode]) map[u.bundle_barcode] = [];
      map[u.bundle_barcode].push(u);
    });
    return map;
  }, [usns]);

  const totals = useMemo(() => ({
    total: usns.length,
    perfect: usns.filter((u) => u.quality_status === 'PERFECT').length,
    damaged: usns.filter((u) => u.quality_status === 'DAMAGED').length,
    refinish: usns.filter((u) => u.quality_status === 'REFINISH').length,
    pending: usns.filter((u) => !u.quality_status).length,
    needsFg: usns.filter((u) => u.needs_fg_assignment).length,
  }), [usns]);

  return (
    <StaffScanGate stationName="Sales Return — Bundle Receipt">
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="sales-return-bundle-receipt">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 min-h-16 py-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/sections/sales-return')}
              className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Sales Return — Bundle Receipt</h1>
              <p className="text-sm text-[#E8DCC8]">USN-level quality routing for returned goods</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            {/* Print method: USB (WebUSB) or Network (Print Bridge) */}
            <div className="flex rounded-md overflow-hidden border border-white/25" data-testid="print-method-toggle">
              <button type="button" onClick={() => changePrintMethod('usb')}
                className={`px-2.5 py-1 text-xs flex items-center gap-1 ${printMethod === 'usb' ? 'bg-white text-[#1A1F2E]' : 'text-[#E8DCC8] hover:bg-white/10'}`}
                data-testid="print-method-usb">
                <Usb className="w-3 h-3" /> USB
              </button>
              <button type="button" onClick={() => changePrintMethod('network')}
                className={`px-2.5 py-1 text-xs flex items-center gap-1 ${printMethod === 'network' ? 'bg-white text-[#1A1F2E]' : 'text-[#E8DCC8] hover:bg-white/10'}`}
                data-testid="print-method-network">
                <NetworkIcon className="w-3 h-3" /> Network
              </button>
            </div>
            {printMethod === 'usb' ? (
              usbDevice ? (
                <Badge variant="outline" className="border-green-400 text-green-400"
                  data-testid="printer-connected-badge">
                  <Usb className="w-3 h-3 mr-1" />
                  {usbDevice.productName || 'Printer connected'}
                </Badge>
              ) : (
                <Button variant="outline" size="sm" onClick={connectPrinter}
                  disabled={!usbSupported}
                  className="border-yellow-400 text-yellow-400 hover:bg-yellow-400/10"
                  data-testid="connect-printer-btn">
                  <Usb className="w-3 h-3 mr-1" />
                  Connect Printer
                </Button>
              )
            ) : (
              <Button variant="outline" size="sm" onClick={() => ensureNetPrinter(true)}
                className="border-cyan-400 text-cyan-300 hover:bg-cyan-400/10"
                data-testid="select-network-printer-btn">
                <Printer className="w-3 h-3 mr-1" />
                {netPrinter ? netPrinter.printerName : 'Select printer'}
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/sections/sales-return/refinish-bundles')}
              data-testid="open-refinish-bundles-btn">
              <Sparkles className="w-4 h-4 mr-2" /> Refinish Bundles
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* HEADER — Customer / Transport / Agent / Reason */}
        {!receipt && (
          <Card data-testid="receipt-header-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Receipt className="w-5 h-5" /> New Receipt — Header
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Customer */}
              <div className="md:col-span-2">
                <Label className="text-xs">Customer *</Label>
                <Popover open={customerPickerOpen} onOpenChange={setCustomerPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox"
                      className="w-full justify-between mt-1 h-10"
                      data-testid="customer-picker-trigger">
                      <span className="truncate">
                        {customerName ? `${customerName}${place ? ` — ${place}` : ''}` : 'Select customer…'}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[480px] p-0" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search customer name / place / GSTIN…"
                        value={customerQuery}
                        onValueChange={setCustomerQuery}
                      />
                      <CommandList className="max-h-[320px]">
                        <CommandEmpty>No customer found.</CommandEmpty>
                        <CommandGroup>
                          {filteredCustomers.map((c) => {
                            const code = c.ginesys_id || c.id || c.customer_code;
                            const name = c.display_name || c.customer_name || c.legal_name || c.trade_name || code;
                            const cplace = c.place || c.billing_place || '';
                            return (
                              <CommandItem
                                key={code}
                                value={`${name} ${cplace} ${c.gst || ''}`}
                                onSelect={() => {
                                  setCustomerCode(code);
                                  setCustomerName(name);
                                  setPlace(cplace);
                                  setCustomerPickerOpen(false);
                                  setCustomerQuery('');
                                }}
                                data-testid={`customer-option-${code}`}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{name}</span>
                                  <span className="text-xs text-gray-500">
                                    {cplace} · {c.gst || code}
                                  </span>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                        {filteredCustomers.length === 500 && (
                          <div className="px-3 py-2 text-[11px] text-gray-400 border-t">
                            Showing first 500 matches — keep typing to narrow down
                          </div>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              {/* Place override */}
              <div>
                <Label className="text-xs">Place *</Label>
                <Input value={place} onChange={(e) => setPlace(e.target.value)}
                  className="mt-1 h-10" placeholder="City / Town"
                  data-testid="place-input" />
              </div>
              {/* Transporter */}
              <div>
                <Label className="text-xs">Transporter</Label>
                <Select value={transporterId}
                  onValueChange={(v) => {
                    setTransporterId(v);
                    const t = transporters.find((x) => x.transport_id === v);
                    setTransporterName(t?.name || '');
                  }}>
                  <SelectTrigger className="mt-1 h-10" data-testid="transporter-trigger">
                    <SelectValue placeholder="Select transporter…" />
                  </SelectTrigger>
                  <SelectContent>
                    {transporters.map((t) => (
                      <SelectItem key={t.transport_id} value={t.transport_id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Freight */}
              <div>
                <Label className="text-xs">Freight Charge (₹)</Label>
                <Input type="number" value={freightCharge}
                  onChange={(e) => setFreightCharge(e.target.value)}
                  className="mt-1 h-10" placeholder="0"
                  data-testid="freight-input" />
              </div>
              {/* Agent */}
              <div>
                <Label className="text-xs">Agent *</Label>
                <Select value={agentName} onValueChange={setAgentName}>
                  <SelectTrigger className="mt-1 h-10" data-testid="agent-trigger">
                    <SelectValue placeholder="Select agent…" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((a) => (
                      <SelectItem key={a} value={a} data-testid={`agent-option-${a}`}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Reason */}
              <div className="md:col-span-1">
                <Label className="text-xs">Return Reason</Label>
                <Select value={returnReason} onValueChange={setReturnReason}>
                  <SelectTrigger className="mt-1 h-10" data-testid="reason-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reasons.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Reason note (optional)</Label>
                <Input value={returnReasonNote} onChange={(e) => setReturnReasonNote(e.target.value)}
                  className="mt-1 h-10" placeholder="Free-text additional context"
                  data-testid="reason-note-input" />
              </div>
              <div className="md:col-span-3 flex justify-end pt-2">
                <Button onClick={createReceipt}
                  disabled={!headerComplete || isCreatingReceipt}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                  data-testid="create-receipt-btn">
                  {isCreatingReceipt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                  Open Receipt
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* RECEIPT IS OPEN — bundle scan + USN scan */}
        {receipt && (
          <>
            {resumedDraft && (
              <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} />
            )}
            <Card data-testid="active-receipt-card">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">{receipt.receipt_id}</CardTitle>
                    <p className="text-sm text-gray-600">
                      <UserIcon className="w-3.5 h-3.5 inline mr-1" />
                      {receipt.customer_name} — {receipt.place}
                      <span className="mx-2">·</span>
                      <Truck className="w-3.5 h-3.5 inline mr-1" />
                      {receipt.transporter_name || '—'} (₹{receipt.freight_charge})
                      <span className="mx-2">·</span>
                      Agent: <strong>{receipt.agent_name}</strong>
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm">
                    <Badge variant="outline">Total {totals.total}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">Perfect {totals.perfect}</Badge>
                    <Badge className="bg-red-100 text-red-700 border-red-300">Damaged {totals.damaged}</Badge>
                    <Badge className="bg-amber-100 text-amber-700 border-amber-300">Refinish {totals.refinish}</Badge>
                    {totals.pending > 0 && <Badge variant="destructive">Untagged {totals.pending}</Badge>}
                    {totals.needsFg > 0 && <Badge variant="destructive">FG missing {totals.needsFg}</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Bundle scan */}
                <form onSubmit={handleAddBundle} className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-gray-500" />
                  <Input ref={bundleInputRef} value={bundleInput}
                    onChange={(e) => setBundleInput(e.target.value)}
                    placeholder="Scan / type bundle barcode and press Enter"
                    className="h-10 max-w-md" data-testid="bundle-scan-input" />
                  <Button type="submit" data-testid="bundle-scan-submit">Add Bundle</Button>
                  <span className="text-xs text-gray-500 ml-2">
                    {receipt.bundles?.length || 0} bundle(s) so far
                  </span>
                </form>

                {receipt.bundles?.length > 0 && (
                  <div className="flex flex-wrap gap-2 items-center">
                    <Label className="text-xs text-gray-500">Active bundle:</Label>
                    {receipt.bundles.map((b) => (
                      <Button key={b} size="sm"
                        variant={activeBundle === b ? 'default' : 'outline'}
                        onClick={() => setActiveBundle(b)}
                        data-testid={`bundle-pill-${b}`}>
                        {b} ({(usnsByBundle[b] || []).length})
                      </Button>
                    ))}
                  </div>
                )}

                {/* USN scan (only when an active bundle is selected) */}
                {activeBundle && (
                  <form onSubmit={handleScanUsn}
                    className="flex items-center gap-2 border-t pt-4">
                    <Scan className="w-4 h-4 text-gray-500" />
                    <Input ref={usnInputRef} value={usnInput}
                      onChange={(e) => setUsnInput(e.target.value)}
                      placeholder={`Scan USN for bundle ${activeBundle}`}
                      className="h-10 max-w-md" data-testid="usn-scan-input"
                      disabled={scanningUsn} />
                    <Button type="submit" disabled={scanningUsn}
                      data-testid="usn-scan-submit">
                      {scanningUsn ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Scan USN'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* USN TABLE */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ListChecks className="w-4 h-4" /> Scanned Pieces
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bundle</TableHead>
                      <TableHead>USN</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Shade / Size</TableHead>
                      <TableHead>Quality</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usns.length === 0 && (
                      <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8">
                        No USNs scanned yet.
                      </TableCell></TableRow>
                    )}
                    {usns.map((u) => (
                      <TableRow key={u.usn_code} data-testid={`usn-row-${u.usn_code}`}>
                        <TableCell className="font-mono text-xs">{u.bundle_barcode}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {u.usn_code}
                          {u.parent_combo_usn && (
                            <Badge variant="outline" className="ml-1 text-[10px]">
                              comp of {u.parent_combo_usn.slice(-6)}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={u.source === 'OLD' ? 'default' : 'outline'} className="text-[10px]">
                            {u.source}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {u.needs_fg_assignment ? (
                            <Button size="sm" variant="outline" className="h-6 text-[10px]"
                              onClick={() => {
                                setFgPromptUsn(u);
                                setFgSku(''); setFgDesign(''); setFgShade(''); setFgSize('');
                              }}
                              data-testid={`assign-fg-${u.usn_code}`}>
                              Assign FG
                            </Button>
                          ) : (u.sku_code || '—')}
                        </TableCell>
                        <TableCell className="text-xs">{u.design_name || '—'}</TableCell>
                        <TableCell className="text-xs">
                          {u.shade || '—'} / {u.size || '—'}
                        </TableCell>
                        <TableCell>
                          <Select value={u.quality_status || ''}
                            onValueChange={(v) => setQuality(u.usn_code, v)}>
                            <SelectTrigger className="h-7 w-[110px] text-xs"
                              data-testid={`quality-trigger-${u.usn_code}`}>
                              <SelectValue placeholder="Set…" />
                            </SelectTrigger>
                            <SelectContent>
                              {QUALITY_OPTIONS.map((q) => (
                                <SelectItem key={q.value} value={q.value}
                                  data-testid={`quality-${q.value}-${u.usn_code}`}>
                                  {q.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          {u.quality_status === 'REFINISH' && !u.parent_combo_usn && (
                            <Button size="sm" variant="ghost"
                              disabled={unbundlingUsn === u.usn_code}
                              onClick={() => unbundleCombo(u.usn_code)}
                              title="Unbundle combo into components for per-piece routing"
                              data-testid={`unbundle-${u.usn_code}`}>
                              {unbundlingUsn === u.usn_code
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <RotateCw className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500"
                            onClick={() => setRemoveUsnTarget(u.usn_code)}
                            data-testid={`remove-usn-${u.usn_code}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* ITEM-WISE SUMMARY + NEW-MRP LABEL REPRINT */}
            <Card data-testid="item-summary-card">
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Tags className="w-4 h-4" /> Item-wise Summary &amp; Label Reprint
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchItemSummary}
                    disabled={summaryLoading} data-testid="refresh-summary-btn">
                    {summaryLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <RefreshCw className="w-4 h-4" />}
                    <span className="ml-1 hidden sm:inline">Refresh</span>
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Compare the <strong>Current MRP</strong> (from Rates Master) with the physical
                  sticker. If they match, no need to reprint. If not, click{' '}
                  <strong>Print Labels</strong> to mint fresh USNs with the new MRP and print them.
                </p>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Design</TableHead>
                      <TableHead>Shade / Size</TableHead>
                      <TableHead className="text-center">Pieces</TableHead>
                      <TableHead>Pricing</TableHead>
                      <TableHead className="text-right">Current MRP</TableHead>
                      <TableHead className="text-right">Print</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemSummary.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">
                        {summaryLoading ? 'Loading summary…' : 'Scan pieces to see the item-wise summary.'}
                      </TableCell></TableRow>
                    )}
                    {itemSummary.map((it, idx) => {
                      const sku = it.sku_code || `__unassigned_${idx}`;
                      const pt = pricingBySku[it.sku_code] || 'TN';
                      const mrp = currentMrpFor(it);
                      const isPrinting = printingSku === it.sku_code;
                      return (
                        <TableRow key={sku} data-testid={`summary-row-${it.sku_code || 'unassigned'}`}>
                          <TableCell className="font-mono text-xs">
                            {it.sku_code || <span className="text-red-500 italic">Unassigned FG</span>}
                            {it.is_combo && (
                              <Badge variant="outline"
                                className="ml-1 text-[10px] bg-purple-50 text-purple-700 border-purple-300"
                                data-testid={`summary-combo-${it.sku_code}`}>
                                <Boxes className="w-3 h-3 mr-0.5" /> COMBO
                              </Badge>
                            )}
                            {it.reprinted > 0 && (
                              <Badge variant="outline"
                                className="ml-1 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
                                {it.reprinted} reprinted
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{it.design_name || '—'}</TableCell>
                          <TableCell className="text-xs">
                            {it.shade || '—'} / {it.size || '—'}
                          </TableCell>
                          <TableCell className="text-center font-semibold"
                            data-testid={`summary-count-${it.sku_code || 'unassigned'}`}>
                            {it.count}
                          </TableCell>
                          <TableCell>
                            {it.available_pricing_types?.length > 0 ? (
                              <Select value={pt}
                                onValueChange={(v) => setPricingBySku((p) => ({ ...p, [it.sku_code]: v }))}>
                                <SelectTrigger className="h-7 w-[84px] text-xs"
                                  data-testid={`summary-pricing-${it.sku_code}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {it.available_pricing_types.map((p) => (
                                    <SelectItem key={p} value={p}>{p}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-semibold"
                            data-testid={`summary-mrp-${it.sku_code || 'unassigned'}`}>
                            {mrp != null
                              ? <span className="inline-flex items-center"><IndianRupee className="w-3 h-3" />{Number(mrp).toFixed(2)}</span>
                              : <span className="text-xs text-gray-400">no rate</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm"
                              disabled={!it.sku_code || isPrinting || mrp == null}
                              onClick={() => handlePrintGroup(it)}
                              className="bg-[#2D5F4F] hover:bg-[#234A3D] h-8"
                              data-testid={`print-labels-${it.sku_code || 'unassigned'}`}>
                              {isPrinting
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Printer className="w-3.5 h-3.5" />}
                              <span className="ml-1">Print Labels</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {printMethod === 'usb' && !usbDevice && itemSummary.length > 0 && (
                  <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-2 mt-3"
                    data-testid="printer-hint">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                    Connect a USB label printer (top-right) — you&apos;ll also be prompted the first time you print.
                  </p>
                )}
                {printMethod === 'network' && !netPrinter && itemSummary.length > 0 && (
                  <p className="text-xs text-cyan-800 bg-cyan-50 border border-cyan-200 rounded px-3 py-2 mt-3"
                    data-testid="network-printer-hint">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                    Network printing selected — pick a printer (top-right), or you&apos;ll be prompted the first time you print.
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setReceipt(null); setUsns([]); setResumedDraft(null); }}
                data-testid="discard-receipt-btn">Discard</Button>
              <Button onClick={finalize} disabled={isFinalizing}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="finalize-receipt-btn">
                {isFinalizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Finalize & Route
              </Button>
            </div>
          </>
        )}

        {/* FG ASSIGNMENT DIALOG */}
        <Dialog open={!!fgPromptUsn} onOpenChange={(open) => {
          if (!open) {
            setFgPromptUsn(null);
            // Re-focus the USN input after the dialog closes so scanning resumes
            setTimeout(() => usnInputRef.current?.focus(), 50);
          }
        }}>
          <DialogContent data-fg-prompt-open="true">
            <DialogHeader>
              <DialogTitle>Assign FG details — {fgPromptUsn?.usn_code}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>FG / SKU code *</Label>
                <Input value={fgSku} onChange={(e) => setFgSku(e.target.value)}
                  placeholder="e.g. MW123710"
                  data-testid="fg-sku-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Design</Label>
                  <Input value={fgDesign} onChange={(e) => setFgDesign(e.target.value)}
                    data-testid="fg-design-input" />
                </div>
                <div>
                  <Label>Shade</Label>
                  <Input value={fgShade} onChange={(e) => setFgShade(e.target.value)}
                    data-testid="fg-shade-input" />
                </div>
              </div>
              <div>
                <Label>Size</Label>
                <Input value={fgSize} onChange={(e) => setFgSize(e.target.value)}
                  data-testid="fg-size-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setFgPromptUsn(null)}>Cancel</Button>
              <Button onClick={submitFgAssignment} data-testid="fg-assign-submit"
                className="bg-[#2D5F4F] hover:bg-[#234A3D]">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* COMBO BOX-STICKER — second print step, optionally on a different printer */}
        <Dialog open={!!pendingBoxPrint} onOpenChange={(open) => { if (!open) handleSkipBox(); }}>
          <DialogContent data-testid="box-sticker-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Boxes className="w-5 h-5 text-purple-600" />
                Print BOX stickers — {pendingBoxPrint?.sku}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <p>
                Product stickers are printed. This is a <strong>combo</strong> item, so it also
                needs <strong>{pendingBoxPrint?.newUsns?.length || 0}</strong> box sticker(s) using
                template <strong>{pendingBoxPrint?.boxTemplate?.template_name || '—'}</strong>.
              </p>
              <p className="text-gray-500">
                Load the box-sticker roll. You can print on the same printer, or pick a different
                (alternate) printer for the box labels.
              </p>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={handleSkipBox}
                data-testid="box-skip-btn">Skip</Button>
              <Button variant="outline" onClick={() => handlePrintBoxStickers(true)}
                disabled={printingSku === pendingBoxPrint?.sku}
                data-testid="box-different-printer-btn">
                <Usb className="w-4 h-4 mr-1" /> Use a different printer
              </Button>
              <Button onClick={() => handlePrintBoxStickers(false)}
                disabled={printingSku === pendingBoxPrint?.sku}
                className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                data-testid="box-print-btn">
                {printingSku === pendingBoxPrint?.sku
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  : <Printer className="w-4 h-4 mr-1" />}
                Print Box Stickers
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Network printer picker (Print Bridge) */}
        <NetworkPrinterDialog
          open={printerPickerOpen}
          onOpenChange={handlePrinterDialogClose}
          onConfirm={handlePrinterPicked}
          title="Select a network printer"
        />
      </main>
    </div>
    <ConfirmRemoveBundleDialog
      barcode={removeUsnTarget}
      itemLabel="USN"
      onConfirm={() => { removeUsn(removeUsnTarget); setRemoveUsnTarget(null); }}
      onCancel={() => setRemoveUsnTarget(null)}
    />
    </StaffScanGate>
  );
};

export default SalesReturnBundleReceiptEntry;
